import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apis from '#store/api';
import { DataApiResponse, DataCollectionApiResponse } from './types';
import { User } from './users';
import { Incident } from './incidents';
import { useEffect } from "react";
import { downloadReportDocx, getUserTimeZone } from '#root/services/utils';

export const AFTER_ACTION_REPORT_KEY = 'afterActionReport';

export interface AfterActionReport {
  id: string;
  reportName: string;
  userId?: string;
  user?: User;
  isSuperAdmin?: boolean;
  improvementPlanTable?: boolean;
  startDate: Date;
  endDate: Date;
  createdDate?: string;
  incidentId: string;
  incident?: Incident;
}

interface CreateAfterActionReportParams {
  report: Omit<AfterActionReport, 'id'>;
}

export interface GetAfterActionReportByIdParams {
  id: string;
}

interface GetAfterActionReportsParams {
  incidentId?: string;
  searchQuery?: string;
  skip: number;
  take?: number;
}

interface DeleteAfterActionReportParams {
  id: string;
}

export const useAfterActionReports = (params: GetAfterActionReportsParams) => {
  return useQuery({
    queryKey: [AFTER_ACTION_REPORT_KEY, params],
    queryFn: async () => {
      const response = await apis.afterActionReport.getAfterActionReportList({ params });
      return response.data as DataCollectionApiResponse<AfterActionReport>;
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
};

export const useAfterActionReportById = (params: GetAfterActionReportByIdParams) => {
  return useQuery({
    queryKey: [AFTER_ACTION_REPORT_KEY, params],
    queryFn: async () => {
      const response = await apis.afterActionReport.getAfterActionReportById({ data: params });
      return response.data as DataApiResponse<AfterActionReport>;
    },
  });
};

export const useCreateAfterActionReport = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, CreateAfterActionReportParams>({
    mutationFn: async (params: CreateAfterActionReportParams) => {
      const response = await apis.afterActionReport.createAfterActionReport({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AFTER_ACTION_REPORT_KEY] });
    },
  });
};

export const useDeleteAfterActionReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteAfterActionReportParams) => {
      const response = await apis.afterActionReport.deleteAfterActionReport({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AFTER_ACTION_REPORT_KEY] });
    },
  });
};

export const useGetAfterActionReportDocument = (reportId: string) => {
  const queryClient = useQueryClient();

  const queryResult = useQuery<Blob, Error, Blob, [string, string]>({
    queryKey: ["afterActionReportDocument", reportId],
    queryFn: async () => {
      if (!reportId.trim()) {
        return Promise.reject(new Error("Invalid reportId"));
      }
      const response = await apis.afterActionReport.getAfterActionReportDocument({
        data: { 
          reportId,
          userTimeZone: getUserTimeZone()
        },
      });
      return response.data;
    },
    enabled: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (queryResult.data) {
      downloadReportDocx(queryResult.data);
      queryClient.removeQueries({ queryKey: ["afterActionReportDocument", reportId] });
    }
  }, [queryResult.data, queryClient, reportId]);

  return queryResult;
};